// ---------------------------------------------------------------------------
// Currency formatting helpers for in-scope quote price surfaces (#148).
//
// Goals:
//   - Display a visible currency symbol/prefix (e.g. "€", "£") for every
//     supported quote currency, derived from the explicit `currency` field
//     on the response — never by parsing the `price` string.
//   - Strip dangling ".0" / ".00" / ".000" for whole amounts (e.g. "1500.00"
//     -> "1500") so prices never render as "1500.0".
//   - Format ranges with the symbol on both sides, e.g. "€1209 – €1342".
//
// The mapping is intentionally explicit (not derived from `Intl.NumberFormat`)
// so the visible symbol stays predictable across browsers and locales. The
// spreadsheet countries drive the minimum required coverage; new currencies
// can be added by extending `CURRENCY_SYMBOLS`.
// ---------------------------------------------------------------------------

export const CURRENCY_SYMBOLS: Record<string, string> = {
  EUR: '€',
  GBP: '£',
  USD: '$',
  CHF: 'CHF',
  BGN: 'лв',
  CZK: 'Kč',
  DKK: 'kr',
  HUF: 'Ft',
  NOK: 'kr',
  PLN: 'zł',
  RON: 'lei',
  SEK: 'kr',
};

export const getCurrencySymbol = (code: string | null | undefined): string => {
  if (!code) return '';
  return CURRENCY_SYMBOLS[code] ?? code;
};

// Strip a trailing ".0" / ".00" / etc. from a price string so the UI never
// shows a dangling ".0" for whole amounts (e.g. "1500.00" -> "1500").
export const formatPriceAmount = (raw: string | number | null | undefined): string => {
  if (raw === null || raw === undefined) return '';
  const str = String(raw).trim();
  if (str === '') return '';
  return str.replace(/\.0+$/, '').replace(/\.$/, '');
};

export interface CurrencyAmountInput {
  price: string | number | null | undefined;
  currency?: string | null;
}

// Format a single price with its currency symbol/prefix (e.g. "€1337").
export const formatPrice = (
  rawPrice: string | number | null | undefined,
  currency: string | null | undefined,
): string => {
  const amount = formatPriceAmount(rawPrice);
  const symbol = getCurrencySymbol(currency);
  if (!amount && !symbol) return '';
  if (!symbol) return amount;
  if (!amount) return symbol;
  return `${symbol}${amount}`;
};

// Format a price range across multiple responses. Uses the explicit
// `currency` field — never parses the `price` string to infer a symbol.
export const formatPriceRange = (responses: CurrencyAmountInput[]): string => {
  if (!responses || responses.length === 0) return '';

  const parsed = responses
    .map((r) => {
      const str = r.price === null || r.price === undefined ? '' : String(r.price);
      // Match the first run of digits/separators. We use this only for the
      // numeric comparison; the displayed symbol comes from the explicit
      // `currency` field, never from `price`. Normalise a comma decimal
      // separator to a dot so parseFloat behaves consistently across locales.
      const match = str.match(/[\d.,]+/);
      if (!match) return null;
      const num = parseFloat(match[0].replace(',', '.'));
      return Number.isFinite(num) ? { num, currency: r.currency ?? null } : null;
    })
    .filter((entry): entry is { num: number; currency: string | null } => entry !== null);

  if (parsed.length === 0) return '';

  const minEntry = parsed.reduce((min, entry) => (entry.num < min.num ? entry : min));
  const maxEntry = parsed.reduce((max, entry) => (entry.num > max.num ? entry : max));

  if (minEntry.num === maxEntry.num) {
    return formatPrice(minEntry.num, minEntry.currency);
  }
  return `${formatPrice(minEntry.num, minEntry.currency)} – ${formatPrice(maxEntry.num, maxEntry.currency)}`;
};
